export const PERMISSION = {
  TEMPLATES_VIEW: "view_notification_templates",
  TEMPLATES_ADD: "add_notification_templates",
  TEMPLATES_EDIT: "edit_notification_templates",
  TEMPLATES_DELETE: "delete_notification_templates",
  HISTORY: "operate_notifications_history",
  PAYLOADS: "operate_notifications_payloads",
  TASKS: "operate_notifications_tasks",
} as const;

export type PermissionCode = (typeof PERMISSION)[keyof typeof PERMISSION];

export type TemplatePermission = {
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
};

export type ModulePermission = {
  isHistory: boolean;
  isPayloads: boolean;
  isTasks: boolean;
  templates: TemplatePermission;
};

export function createPermissionSet(codes: string[]): Set<string> {
  return new Set(codes);
}

export function hasPermission(set: Set<string>, code: string): boolean {
  return set.has(code);
}

export function resolveTemplatePermission(set: Set<string>): TemplatePermission {
  const canAdd = set.has(PERMISSION.TEMPLATES_ADD);
  const canEdit = set.has(PERMISSION.TEMPLATES_EDIT);
  const canDelete = set.has(PERMISSION.TEMPLATES_DELETE);
  const canView =
    set.has(PERMISSION.TEMPLATES_VIEW) || canEdit || canAdd || canDelete;

  return {
    canView,
    canAdd,
    canEdit,
    canDelete,
  };
}

export function hasTemplatesAccess(set: Set<string>): boolean {
  return resolveTemplatePermission(set).canView;
}

export function parseModulePermissions(codes: string[]): ModulePermission {
  const set = createPermissionSet(codes);

  return {
    isHistory: set.has(PERMISSION.HISTORY),
    isPayloads: set.has(PERMISSION.PAYLOADS),
    isTasks: set.has(PERMISSION.TASKS),
    templates: resolveTemplatePermission(set),
  };
}

export function hasRoutePermission(
  permissionSet: Set<string>,
  patternPath: string,
  pathAuthMapping: Map<string, string>,
  pathAuthWhiteList: string[],
  matchPath: (pattern: string, pathname: string) => unknown
): boolean {
  if (pathAuthWhiteList.some((pattern) => Boolean(matchPath(pattern, patternPath)))) {
    return true;
  }

  const requiredCode = pathAuthMapping.get(patternPath);
  if (!requiredCode) {
    return false;
  }
  // Templates route: view, or any of add/edit/delete, grants access
  if (requiredCode === PERMISSION.TEMPLATES_VIEW) {
    return hasTemplatesAccess(permissionSet);
  }
  return permissionSet.has(requiredCode);
}

/** Same order as SideMenu: Templates → Payloads → Tasks → History */
export const NOTIFICATION_ROUTE_CANDIDATES = [
  { path: "/notification/templates", permission: PERMISSION.TEMPLATES_VIEW },
  { path: "/notification/payloads", permission: PERMISSION.PAYLOADS },
  { path: "/notification/tasks", permission: PERMISSION.TASKS },
  { path: "/notification/history", permission: PERMISSION.HISTORY },
] as const;

export function resolveFallbackNotificationPath(
  permissionSet: Set<string>
): string | null {
  for (const route of NOTIFICATION_ROUTE_CANDIDATES) {
    const allowed =
      route.permission === PERMISSION.TEMPLATES_VIEW
        ? hasTemplatesAccess(permissionSet)
        : permissionSet.has(route.permission);
    if (allowed) {
      return route.path;
    }
  }
  return null;
}
